import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

export type FormStudioActions = {
  save?: () => void | Promise<void>;
  preview?: () => void;
  publish?: () => void | Promise<void>;
  saving?: boolean;
  publishing?: boolean;
  dirty?: boolean;
  statusLabel?: string;
};

type FormStudioContextValue = {
  registerActions: (actions: FormStudioActions | null) => void;
};

const FormStudioContext = createContext<FormStudioContextValue | null>(null);

export const FormStudioActionsProvider: React.FC<{
  registerActions: (actions: FormStudioActions | null) => void;
  children: React.ReactNode;
}> = ({ registerActions, children }) => {
  const value = useMemo(() => ({ registerActions }), [registerActions]);
  return <FormStudioContext.Provider value={value}>{children}</FormStudioContext.Provider>;
};

export function useFormStudioActions(actions: FormStudioActions | null) {
  const context = useContext(FormStudioContext);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  useEffect(() => {
    if (!context) return;
    if (!actions) {
      context.registerActions(null);
      return;
    }
    context.registerActions({
      save: actions.save ? () => actionsRef.current?.save?.() : undefined,
      preview: actions.preview ? () => actionsRef.current?.preview?.() : undefined,
      publish: actions.publish ? () => actionsRef.current?.publish?.() : undefined,
      saving: actions.saving,
      publishing: actions.publishing,
      dirty: actions.dirty,
      statusLabel: actions.statusLabel,
    });
    return () => context.registerActions(null);
  }, [
    context,
    Boolean(actions?.save),
    Boolean(actions?.preview),
    Boolean(actions?.publish),
    actions?.saving,
    actions?.publishing,
    actions?.dirty,
    actions?.statusLabel,
  ]);
}

export function useIsInsideFormStudio(): boolean {
  return useContext(FormStudioContext) != null;
}
